import React from 'react';
import { toDecimalPoint } from '../../../utils/decimalUtils';
import DecimalInput from '../../fields/DecimalInput';

const CauseSignModal = ({
  isModalOpen,
  selectedRowId,
  modalData,
  handbooks,
  loadedSigns,
  isLoadingSigns,
  openPicker,
  openYearPicker,
  openSignsPicker,
  addModalRow,
  removeModalRow,
  closeModal,
  handleModalDone,
  modalScrollRef,
  modalScrollPosition,
  handleModalSliderChange,
  handleModalScroll,
  updateModalData,
  addPriznakToItem,
  removePriznakFromItem,
  sosnyakValue = 0,
  submitError = ''
}) => {
  if (!isModalOpen) return null;

  const hasValue = (value) => String(value ?? '').trim() !== '';
  const formatCauseLabel = (cause) => {
    if (!cause) return '';
    const code = String(cause.code ?? '').trim();
    return code ? `${cause.name} (${code})` : cause.name;
  };
  const formatSignLabel = (priznakId, signsOptions) => {
    const signOption = (Array.isArray(signsOptions) ? signsOptions : []).find(
      s => String(s.value) === String(priznakId)
    );
    const baseLabel = signOption?.label || String(priznakId);
    const handbook = handbooks.find(h => h.id === 9);
    const handbookSign = handbook?.references?.find(r => String(r.id) === String(priznakId));
    const code = String(signOption?.code ?? handbookSign?.code ?? '').trim();

    if (!code) return baseLabel;
    if (baseLabel.includes(`(${code})`)) return baseLabel;
    return `${baseLabel} (${code})`;
  };

  // Функция для проверки, можно ли заполнять специальную долю для данной причины
  const canFillSpecificDola = (prichinaId) => {
    if (!prichinaId) return false;
    
    const handbook = handbooks.find(h => h.id === 10);
    if (!handbook || !handbook.references) return false;
    
    const selectedCause = handbook.references.find(r => r.id.toString() === String(prichinaId));
    if (!selectedCause) return false;
    
    // Проверяем тип причины
    const causeType = selectedCause.type || selectedCause.disease_type || '';
    return causeType === 'DISEASES' || causeType === 'STEM_PESTS' || causeType === 'CONDITIONAL_ROTTENING' || causeType === 'OTHER';
  };

  // Функция для определения названия столбца специальной доли на основе последней выбранной причины
  const getSpecificDolaColumnName = () => {
    if (!modalData || !Array.isArray(modalData) || modalData.length === 0) {
      return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
    }

    // Находим последнюю выбранную причину (последний элемент с заполненной prichina)
    let lastCause = null;
    for (let i = modalData.length - 1; i >= 0; i--) {
      if (modalData[i].prichina) {
        lastCause = modalData[i];
        break;
      }
    }

    if (!lastCause || !lastCause.prichina) {
      return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
    }

    const handbook = handbooks.find(h => h.id === 10);
    if (!handbook || !handbook.references) {
      return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
    }

    const selectedCause = handbook.references.find(r => r.id.toString() === String(lastCause.prichina));
    if (!selectedCause) {
      return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
    }

    const causeType = selectedCause.type || '';
    const diseaseType = selectedCause.disease_type || '';

    // CONDITIONAL_ROTTENING
    if (causeType === 'CONDITIONAL_ROTTENING') {
      // Если сосняк (sosnyakValue >= 6): категории 2, 3, 4
      // Иначе: 2, 3, 4, 5а, 5б, 5в
      if (sosnyakValue >= 6) {
        return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
      } else {
        return 'Доля повреждённых деревьев 2, 3, 4, 5а, 5б, 5в категории состояния от запаса породы';
      }
    }

    // STEM_PESTS
    if (causeType === 'STEM_PESTS') {
      return 'Доля заселенных деревьев от запаса породы';
    }

    // OTHER (прочие вредители)
    if (causeType === 'OTHER') {
      return 'Доля повреждённых деревьев 2, 3, 4, 5а, 5б, 5в, 5г, 5д, 5е категории состояния от запаса породы';
    }

    // DISEASES
    if (causeType === 'DISEASES') {
      // Проверяем disease_type
      if (['40.1', '40.2', '40.3', '40.4', '40.5'].includes(diseaseType)) {
        return 'Доля повреждённых деревьев 2, 3, 4, 5а, 5б, 5в категории состояния от запаса породы';
      }
      
      if (diseaseType === '40.6') {
        return 'Доля повреждённых деревьев 4, 5а, 5б, 5в категории состояния от запаса породы';
      }
      
      // Для остальных значений DISEASES
      return 'Доля повреждённых деревьев 2, 3, 4, 5а, 5б, 5в, 5г, 5д, 5е категории состояния от запаса породы';
    }

    // По умолчанию
    return 'Доля повреждённых деревьев 2, 3, 4 категории состояния от запаса породы';
  };

  const isRowStarted = (item) => {
    const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
    return Boolean(
      hasValue(item.prichina) ||
      hasValue(item.godpovr) ||
      priznaki.length > 0 ||
      hasValue(item.prizn) ||
      hasValue(item.dolaprizn) ||
      hasValue(item.specificDolaPrizn)
    );
  };

  const isRowComplete = (item) => {
    if (!isRowStarted(item)) {
      return true;
    }

    const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
    const hasPriznak = priznaki.length > 0 || hasValue(item.prizn);
    const hasDolaprizn = hasValue(item.dolaprizn);
    const requiresSpecificDola = canFillSpecificDola(item.prichina);
    const hasSpecificDola = hasValue(item.specificDolaPrizn);

    if (!hasPriznak || !hasDolaprizn) {
      return false;
    }

    if (requiresSpecificDola && !hasSpecificDola) {
      return false;
    }

    return true;
  };

  const validationMessages = Array.isArray(modalData)
    ? modalData.flatMap((item, index) => {
        if (!isRowStarted(item) || isRowComplete(item)) {
          return [];
        }

        const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
        const hasPriznak = priznaki.length > 0 || hasValue(item.prizn);
        const hasDolaprizn = hasValue(item.dolaprizn);
        const requiresSpecificDola = canFillSpecificDola(item.prichina);
        const hasSpecificDola = hasValue(item.specificDolaPrizn);
        const rowLabel = `Строка ${index + 1}`;
        const messages = [];

        if (!hasPriznak) {
          messages.push(`${rowLabel}: не заполнено поле "Признак"`);
        }

        if (!hasDolaprizn) {
          messages.push(`${rowLabel}: не заполнено поле "Доля повреждённых деревьев от запаса порода"`);
        }

        if (requiresSpecificDola && !hasSpecificDola) {
          messages.push(`${rowLabel}: не заполнено поле "${getSpecificDolaColumnName()}"`);
        }

        return messages;
      })
    : [];

  const isDoneDisabled = !Array.isArray(modalData) || modalData.some(item => !isRowComplete(item));

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        width: '98vw',
        maxWidth: '1700px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          borderBottom: '1px solid #eee',
          paddingBottom: '10px'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Причина/признак ослабления, повреждения насаждения (Строка {selectedRowId})
          </h3>
          <button
            onClick={closeModal}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {(submitError || validationMessages.length > 0) && (
          <div style={{
            marginBottom: '16px',
            padding: '10px 12px',
            backgroundColor: '#fff5f5',
            border: '1px solid #f5c2c7',
            borderRadius: '6px',
            color: '#b42318',
            fontSize: '13px'
          }}>
            {[submitError, ...validationMessages].filter(Boolean).join('; ')}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <div 
            ref={modalScrollRef}
            onScroll={handleModalScroll}
            style={{ 
              overflowX: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          >
            <table className="table" style={{ marginBottom: 0, minWidth: '1100px' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: '200px', backgroundColor: '#f8f9fa' }}>Причина</th>
                  <th style={{ minWidth: '150px', backgroundColor: '#f8f9fa' }}>Год ослабления, повреждения насаждения</th>
                  <th style={{ minWidth: '200px', backgroundColor: '#f8f9fa' }}>Признак</th>
                  <th style={{ minWidth: '200px', backgroundColor: '#f8f9fa' }}>Доля повреждённых деревьев от запаса породы</th>
                  <th style={{ minWidth: '300px', backgroundColor: '#f8f9fa' }}>{getSpecificDolaColumnName()}</th>
                  <th style={{ width: '120px', backgroundColor: '#f8f9fa' }}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map((item, index) => (
                  <tr key={item.id}>
                    <td>
                      <div 
                        className="reference-cell"
                        onClick={() => openPicker('prichina', 10, item.id)}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          minHeight: '40px',
                          cursor: 'pointer',
                          background: 'transparent',
                          border: 'none',
                          padding: '0',
                          margin: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span style={{ 
                          color: item.prichina ? '#000' : '#6c757d',
                          fontSize: '14px',
                          flex: 1,
                          padding: '8px'
                        }}>
                          {(() => {
                            const handbook = handbooks.find(h => h.id === 10);
                            if (handbook && handbook.references && item.prichina) {
                              const selectedOption = handbook.references.find(r => r.id.toString() === item.prichina);
                              return selectedOption ? formatCauseLabel(selectedOption) : 'Выберите причину';
                            }
                            return 'Выберите причину';
                          })()}
                        </span>
                        <span style={{ color: '#6c757d', fontSize: '12px', paddingRight: '8px' }}>☰</span>
                      </div>
                    </td>
                    <td>
                      <div 
                        className="reference-cell"
                        onClick={() => openYearPicker(item.id)}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          minHeight: '40px',
                          cursor: 'pointer',
                          background: 'transparent',
                          border: 'none',
                          padding: '0',
                          margin: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span style={{ 
                          color: item.godpovr ? '#000' : '#6c757d',
                          fontSize: '14px',
                          flex: 1,
                          padding: '8px'
                        }}>
                          {item.godpovr || 'Выберите год'}
                        </span>
                        <span style={{ color: '#6c757d', fontSize: '12px', paddingRight: '8px' }}>☰</span>
                      </div>
                    </td>
                    <td>
                      <div 
                        style={{ 
                          width: '100%',
                          minHeight: '40px',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px'
                        }}
                      >
                        {(() => {
                          const priznaki = Array.isArray(item.priznaki) ? item.priznaki : [];
                          const handbook = handbooks.find(h => h.id === 10);
                          let causeName = '';
                          if (handbook && handbook.references && item.prichina) {
                            const selectedCause = handbook.references.find(r => r.id.toString() === item.prichina);
                            causeName = selectedCause ? selectedCause.name : '';
                          }
                          const signsOptions = causeName && loadedSigns[causeName] ? loadedSigns[causeName] : [];
                          
                          return (
                            <>
                              {priznaki.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {priznaki.map((priznakId, idx) => {
                                    const signLabel = formatSignLabel(priznakId, signsOptions);
                                    return (
                                      <span
                                        key={idx}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 8px',
                                          backgroundColor: '#e9ecef',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          color: '#000'
                                        }}
                                      >
                                        {signLabel}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (removePriznakFromItem) {
                                              removePriznakFromItem(item.id, priznakId);
                                            }
                                          }}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#dc3545',
                                            cursor: 'pointer',
                                            padding: '0',
                                            margin: '0',
                                            fontSize: '14px',
                                            lineHeight: '1',
                                            fontWeight: 'bold'
                                          }}
                                          title="Удалить признак"
                                        >
                                          ×
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span style={{ 
                                  color: isLoadingSigns && item.prichina ? '#000' : '#6c757d',
                                  fontSize: '14px'
                                }}>
                                  {isLoadingSigns && item.prichina ? 'Загрузка признаков...' : 'Выберите признак'}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  if (item.prichina) {
                                    openSignsPicker(item.id);
                                  } else {
                                    alert('Сначала выберите причину');
                                  }
                                }}
                                disabled={!item.prichina || isLoadingSigns}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  backgroundColor: item.prichina && !isLoadingSigns ? '#6c757d' : '#adb5bd',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: item.prichina && !isLoadingSigns ? 'pointer' : 'not-allowed',
                                  marginTop: priznaki.length > 0 ? '4px' : '0'
                                }}
                              >
                                {priznaki.length > 0 ? '+ Добавить признак' : 'Выбрать признак'}
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      <DecimalInput
                        value={item.dolaprizn || ''}
                        onChange={(e) => {
                          const value = toDecimalPoint(e.target.value);
                          if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
                            if (updateModalData) {
                              updateModalData(item.id, 'dolaprizn', value);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = toDecimalPoint(e.target.value);
                          if (value !== '' && (Number(value) < 0 || Number(value) > 100)) {
                            e.target.value = '';
                            if (updateModalData) {
                              updateModalData(item.id, 'dolaprizn', '');
                            }
                          }
                        }}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          minHeight: '40px',
                          textAlign: 'center',
                          background: 'transparent',
                          border: 'none',
                          padding: '8px',
                          margin: '0',
                          color: item.dolaprizn ? '#000' : '#6c757d',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        placeholder="0-100"
                      />
                    </td>
                    <td>
                      <DecimalInput
                        value={item.specificDolaPrizn || ''}
                        onChange={(e) => {
                          const value = toDecimalPoint(e.target.value);
                          if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
                            if (updateModalData) {
                              updateModalData(item.id, 'specificDolaPrizn', value);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = toDecimalPoint(e.target.value);
                          if (value !== '' && (Number(value) < 0 || Number(value) > 100)) {
                            e.target.value = '';
                            if (updateModalData) {
                              updateModalData(item.id, 'specificDolaPrizn', '');
                            }
                          }
                        }}
                        disabled={!canFillSpecificDola(item.prichina)}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          minHeight: '40px',
                          textAlign: 'center',
                          background: canFillSpecificDola(item.prichina) ? 'transparent' : '#f8f9fa',
                          border: 'none',
                          padding: '8px',
                          margin: '0',
                          color: item.specificDolaPrizn ? '#000' : '#6c757d',
                          fontSize: '14px',
                          outline: 'none',
                          cursor: canFillSpecificDola(item.prichina) ? 'text' : 'not-allowed',
                          opacity: canFillSpecificDola(item.prichina) ? 1 : 0.6
                        }}
                        placeholder={canFillSpecificDola(item.prichina) ? "0-100" : "—"}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          onClick={addModalRow}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: '1px solid #6c757d',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Добавить строку"
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => removeModalRow(item.id)}
                          disabled={modalData.length === 1}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: '1px solid #6c757d',
                            borderRadius: '4px',
                            cursor: modalData.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: modalData.length === 1 ? 0.5 : 1
                          }}
                          title="Удалить строку"
                        >
                          -
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Слайдер для прокрутки таблицы */}
          <div style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{ 
              fontSize: '12px', 
              color: '#6c757d',
              minWidth: '60px'
            }}>
              Прокрутка:
            </span>
            <input
              type="range"
              min="0"
              max="100"
              value={modalScrollPosition}
              onChange={handleModalSliderChange}
              className="table-scroll-slider"
              style={{
                flex: 1
              }}
            />
            <span style={{
              fontSize: '12px',
              color: '#6c757d',
              minWidth: '30px',
              textAlign: 'right'
            }}>
              {Math.round(modalScrollPosition)}%
            </span>
          </div>
          
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '10px',
          marginTop: '20px',
          paddingTop: '15px',
          borderTop: '1px solid #eee'
        }}>
          <button 
            onClick={closeModal}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Отмена
          </button>
          <button 
            onClick={handleModalDone}
            disabled={isDoneDisabled}
            style={{
              padding: '8px 16px',
              backgroundColor: isDoneDisabled ? '#adb5bd' : '#6c757d',
              color: 'white',
              border: `1px solid ${isDoneDisabled ? '#adb5bd' : '#6c757d'}`,
              borderRadius: '4px',
              cursor: isDoneDisabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isDoneDisabled ? 0.7 : 1
            }}
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
};

export default CauseSignModal;

